import React, { useState } from "react";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from "recharts";

import Button from "components/ui/Button";

const chartData = [
  { month: "Apr \'25", revenue: 48200, members: 312, newMembers: 28 },
  { month: "May \'25", revenue: 51800, members: 328, newMembers: 34 },
  { month: "Jun \'25", revenue: 49600, members: 319, newMembers: 22 },
  { month: "Jul \'25", revenue: 55400, members: 341, newMembers: 41 },
  { month: "Aug \'25", revenue: 58900, members: 358, newMembers: 38 },
  { month: "Sep \'25", revenue: 62100, members: 374, newMembers: 45 },
  { month: "Oct \'25", revenue: 59700, members: 367, newMembers: 29 },
  { month: "Nov \'25", revenue: 64300, members: 389, newMembers: 52 },
  { month: "Dec \'25", revenue: 71200, members: 412, newMembers: 61 },
  { month: "Jan \'26", revenue: 68500, members: 401, newMembers: 44 },
  { month: "Feb \'26", revenue: 73800, members: 428, newMembers: 58 },
  { month: "Mar \'26", revenue: 76400, members: 443, newMembers: 67 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-3 shadow-[var(--shadow-lg)] min-w-[160px]">
      <p className="text-xs font-semibold text-[var(--color-foreground)] mb-2 font-[var(--font-caption)]">{label}</p>
      {payload?.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4 text-xs font-[var(--font-caption)]">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry?.color }} />
            <span className="text-[var(--color-muted-foreground)]">{entry?.name}</span>
          </span>
          <span className="font-medium text-[var(--color-foreground)]">
            {entry?.name === "Revenue" ? `$${entry?.value?.toLocaleString()}` : entry?.value}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function RevenueMemberChart() {
  const [activeFilter, setActiveFilter] = useState("all");
  const filters = [
    { key: "all", label: "All Branches" },
    { key: "downtown", label: "Downtown" },
    { key: "westside", label: "Westside" },
    { key: "northpark", label: "North Park" },
  ];

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 md:p-5 shadow-[var(--shadow-sm)]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="text-sm md:text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Revenue & Member Growth</h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">12-month trend with acquisition overlay</p>
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
          {filters?.map(f => (
            <button
              key={f?.key}
              onClick={() => setActiveFilter(f?.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium font-[var(--font-caption)] whitespace-nowrap transition-all flex-shrink-0 ${
                activeFilter === f?.key
                  ? "bg-[var(--color-primary)] text-white"
                  : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              {f?.label}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-64 md:h-72 lg:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000)?.toFixed(0)}k`} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "var(--color-muted-foreground)", fontFamily: "var(--font-caption)" }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: "12px", fontFamily: "var(--font-caption)", paddingTop: "12px" }} />
            <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="var(--color-primary)" radius={[4, 4, 0, 0]} opacity={0.85} />
            <Line yAxisId="right" type="monotone" dataKey="members" name="Active Members" stroke="var(--color-accent)" strokeWidth={2.5} dot={false} />
            <Line yAxisId="right" type="monotone" dataKey="newMembers" name="New Members" stroke="var(--color-success)" strokeWidth={2} dot={false} strokeDasharray="4 2" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-end mt-3 gap-2">
        <Button variant="outline" size="xs" iconName="Download" iconPosition="left" iconSize={13}>Export</Button>
      </div>
    </div>
  );
}